import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import useOrgScope from "../../utils/hooks/useOrgScope";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import { getCoverage } from "../../services/analyticsService";
import { hasPermission, hasPermissionAt, OBSERVATION_PERMISSION_CODES } from "../../utils/permissions";
import { fullName } from "../../utils/helpers/userDisplay";
import FilterSelect from "../../components/forms/FilterSelect";
import Skeleton, { SkeletonStatRow } from "../../components/Skeleton/Skeleton";
import ScheduleObservationModal from "../../components/scheduling/ScheduleObservationModal";
import CoverageTiles from "./CoverageTiles";
import OverdueQueue from "./OverdueQueue";
import PeriodSelect from "./PeriodSelect";
import PersonCards from "./PersonCards";
import PersonTable from "./PersonTable";
import SchoolRollup from "./SchoolRollup";
import VisitsDrawer from "./VisitsDrawer";
import {
  CARD,
  FOCUS_RING,
  META_LABEL,
  PRIMARY_BTN,
  STALE,
} from "./coverageStyles";

// Sent with every request rather than mirrored from the backend default: the
// "N–M of T" line and the page count are arithmetic on this number, so reading
// it from a server constant that is free to change would make the footer lie
// without anything here failing.
const COVERAGE_PAGE_SIZE = 20;

// The filter absorbed the tab strip: «кто проводит» and «кого посещают» listed
// the SAME eleven people at this school — one roster, two labels — so the only
// thing the tabs changed was which of two numbers a row was sorted and coloured
// by. These five options change WHICH PEOPLE are listed, which is the question
// the tabs were being asked to answer and could not.
const FILTERS = ["all", "uncovered", "none_conducted", "overdue", "stale"];
const ORDERINGS = ["attention", "name", "last_activity"];

// A value that is not in the enum is dropped rather than sent on: the server
// already falls back silently, and a URL someone edited by hand should show the
// page it actually rendered.
const oneOf = (allowed, value, fallback) => (allowed.includes(value) ? value : fallback);

function ErrorCard({ children }) {
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm font-semibold text-rose-700">
      {children}
    </div>
  );
}

function Band({ tone, title, body, action }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    slate: "border-slate-200 bg-white text-slate-700",
  };
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border px-5 py-4 ${tones[tone]}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-black">{title}</p>
        {body && <p className="mt-0.5 text-xs font-medium opacity-80">{body}</p>}
      </div>
      {action}
    </div>
  );
}

export default function Coverage() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { scopeMode, effectiveOrgUuid, setSelectedOrgUuid } = useOrgScope();
  const canSchedule = hasPermission(user, OBSERVATION_PERMISSION_CODES.CREATE);
  // At THIS school: an admin elsewhere who only teaches here may not delete.
  const canDelete = hasPermissionAt(
    user,
    OBSERVATION_PERMISSION_CODES.MANAGE,
    effectiveOrgUuid,
  );

  // Every control lives in the URL, so ?filter=uncovered is a link an admin can
  // paste to a principal and get the same screen, not a description of one. A
  // bookmarked ?side= from before the merge is simply absent from this list and
  // dropped — the server ignores it too, and both ends now answer with the one
  // roster it used to split in half.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = oneOf(FILTERS, searchParams.get("filter"), "all");
  const ordering = oneOf(ORDERINGS, searchParams.get("ordering"), "attention");
  const periodParam = searchParams.get("period") || "";
  const urlSearch = searchParams.get("search") || "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const [search, setSearch] = useState(urlSearch);
  const debouncedSearch = useDebouncedValue(search, 300);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [refetching, setRefetching] = useState(false);
  // `null` = closed; otherwise the person the visit is being assigned TO, or
  // `{}` for "somebody, pick them in the modal". Holding the person here is
  // what makes the button an assignment rather than a booking: the modal
  // defaults main_observer to the caller, so an admin who scheduled from this
  // page used to have the visit credited to themselves — the one number this
  // whole screen exists to get right.
  const [schedule, setSchedule] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const setParams = useCallback(
    (patch, { resetPage = true } = {}) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      if (resetPage) next.delete("page");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  // The box is typed into locally and mirrored to the URL on the debounce, with
  // `replace` so a five-letter name does not leave five entries in the back
  // stack. The fetch reads the local debounced value, so the request and the
  // keystrokes can never be one render apart.
  useEffect(() => {
    if (debouncedSearch === urlSearch) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set("search", debouncedSearch);
    else next.delete("search");
    next.delete("page");
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, urlSearch, searchParams, setSearchParams]);

  // The scope decides the request BEFORE it is made. While the subtree is
  // unknown every account looks like a single school, which is exactly how a
  // network-sized roster request escapes — so "loading" asks for nothing.
  useEffect(() => {
    if (scopeMode === "loading") return undefined;
    if (scopeMode === "unavailable") {
      setStatus("unavailable");
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setRefetching(true);

    const params = {
      organization: effectiveOrgUuid,
      period: periodParam || undefined,
      filter,
      search: debouncedSearch || undefined,
      ordering,
      page,
      page_size: COVERAGE_PAGE_SIZE,
    };

    // ONE request. There used to be two — the second existed only to fetch the
    // opposite side's totals for the tiles, because the roster itself was
    // side-relative and a tile that flipped meaning with the tab could state
    // neither half of the question. `totals` now answers for both ends off one
    // roster, so the mirror call and the merge it needed are both gone.
    getCoverage({ params, signal })
      .then((payload) => {
        if (signal.aborted) return;
        setData(payload);
        setStatus("ready");
        setRefetching(false);
      })
      .catch((err) => {
        if (signal.aborted || err?.code === "ERR_CANCELED") return;
        // The endpoint demands observations.view_all on top of the school-view
        // permission the route and the nav item gate on, so a holder of one and
        // not the other arrives here legitimately. Name that refusal instead of
        // reporting a network failure they would try to reload out of.
        setStatus(err?.response?.status === 403 ? "forbidden" : "error");
        setRefetching(false);
      });

    return () => controller.abort();
  }, [
    scopeMode,
    effectiveOrgUuid,
    periodParam,
    filter,
    ordering,
    page,
    debouncedSearch,
    reloadKey,
  ]);

  const totals = data?.totals || null;
  const rows = data?.rows || [];
  const guests = data?.guests || [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / COVERAGE_PAGE_SIZE));
  const isNetwork = (data?.scope?.school_count ?? 0) > 1;

  // The two gap options render the SAME keys as the tile sub-lines above them,
  // so the tile reads as the answer and the option as the action on it. Two
  // strings that merely matched in Russian would diverge the first time anyone
  // edited one of the three catalogs.
  //
  // «Требуют внимания» is gone: measured on the live school it returned all 11
  // rows — an option that selects everyone selects nothing — and it duplicated
  // the default sort, which puts the same people on top without hiding anyone.
  const filterOptions = useMemo(
    () => [
      { value: "all", label: t("coverage.filter.all") },
      { value: "uncovered", label: t("coverage.filter.uncovered") },
      { value: "none_conducted", label: t("coverage.filter.noneConducted") },
      { value: "overdue", label: t("coverage.filter.overdue") },
      { value: "stale", label: t("coverage.filter.stale") },
    ],
    [t],
  );

  const orderingOptions = useMemo(
    () => [
      { value: "attention", label: t("coverage.ordering.attention") },
      { value: "name", label: t("coverage.ordering.name") },
      { value: "last_activity", label: t("coverage.ordering.lastActivity") },
    ],
    [t],
  );

  // A click on a number asks about that number's own column. A click on the
  // NAME has no column, so it opens on the teacher group — the half of the
  // question this screen exists for — and the drawer's own switch reaches the
  // other, falling back on its own when the person holds only one role.
  const openVisits = (row, relation) => setDrawer({ row, relation: relation || "received" });

  const isFiltered = filter !== "all" || Boolean(debouncedSearch);
  const zeroActivity =
    totals && (totals.official || 0) === 0 && (totals.peer || 0) === 0 && (totals.overdue || 0) === 0;
  const allClear = totals && (totals.uncovered || 0) === 0 && (totals.overdue || 0) === 0;
  // Ordering=attention sorts the WHOLE table in SQL, so the first row of page 1
  // really is the roster's minimum. Under a filter or on any later page it is
  // only the minimum of what happens to be on screen, so the band keeps its
  // headline and drops the name rather than name the wrong person.
  const leastActive =
    allClear && !isFiltered && ordering === "attention" && page === 1 && rows.length > 0
      ? rows[0]
      : null;

  if (scopeMode === "loading" || (status === "loading" && !data)) {
    return (
      <div className="min-h-full w-full bg-slate-50 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <Skeleton className="h-4 w-80" />
          <Skeleton className="h-[38px] w-64 rounded-xl" />
          <SkeletonStatRow count={4} />
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-96 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-slate-50 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* No page subtitle. BaseLayout's header carries the title, the tiles
            say what is counted, and the legend at the foot states the counting
            rule — a sentence restating all three above them was a caption on a
            screen that already explains itself. */}
        {status === "unavailable" ? (
          <ErrorCard>{t("coverage.scopeUnavailable")}</ErrorCard>
        ) : status === "forbidden" ? (
          <ErrorCard>{t("coverage.forbidden")}</ErrorCard>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <PeriodSelect
                periods={data?.periods || []}
                period={data?.period}
                value={periodParam}
                onChange={(key) => setParams({ period: key })}
                fellBack={!periodParam && data?.period?.key === "days30"}
                isLoading={refetching}
              />
              {canSchedule && (
                <button type="button" onClick={() => setSchedule({})} className={PRIMARY_BTN}>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t("coverage.assignVisit")}
                </button>
              )}
            </div>

            {status === "error" && (
              <ErrorCard>
                <span>{t("coverage.loadFailed")}</span>
                <button
                  type="button"
                  onClick={() => setReloadKey((k) => k + 1)}
                  className={`ml-3 rounded-md px-1 underline ${FOCUS_RING}`}
                >
                  {t("common.retry")}
                </button>
              </ErrorCard>
            )}

            {status !== "error" && <CoverageTiles totals={totals} />}

            {/* Outside the error branch on purpose: the queue is fed by the
                observations list, so a coverage failure has no bearing on it,
                and it is the one control on this page that cannot be found
                anywhere else in the product. */}
            <OverdueQueue
              organizationUuid={effectiveOrgUuid}
              period={data?.period}
              canAct={canSchedule}
              onChanged={() => setReloadKey((k) => k + 1)}
            />

            {status !== "error" && (
              <>
                {zeroActivity && !isNetwork && (
                  <Band
                    tone="slate"
                    title={t("coverage.zeroTitle")}
                    body={t("coverage.zeroBody")}
                    action={
                      canSchedule ? (
                        <button type="button" onClick={() => setSchedule({})} className={PRIMARY_BTN}>
                          {t("coverage.assignVisit")}
                        </button>
                      ) : null
                    }
                  />
                )}

                {allClear && !zeroActivity && (
                  <Band
                    tone="emerald"
                    title={t("coverage.allClear")}
                    // The conducted figure, named as such. With nobody
                    // uncovered and nothing overdue the attention sort's
                    // leading band is "conducted nothing", so this row is the
                    // quietest OBSERVER — and on a screen that now shows both
                    // roles at once, "fewest visits" had to say which.
                    body={
                      leastActive
                        ? t("coverage.allClearLeastConducted", {
                            name: fullName(leastActive.person),
                            count: leastActive.conducted_total ?? 0,
                          })
                        : undefined
                    }
                  />
                )}

                {/* One card, no tab strip. The two tabs listed the same people
                    with the same numbers and both badges read 11; what actually
                    changed was the meaning of one column heading. Two real date
                    columns inside the table bought its deletion, and the four
                    controls stay four — the filter took the tabs' place. */}
                <section className={`${CARD} overflow-hidden`}>
                  {isNetwork ? (
                    <SchoolRollup schools={data?.schools || []} onPick={setSelectedOrgUuid} />
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
                        <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
                          <span className="sr-only">{t("coverage.search")}</span>
                          <svg
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                          </svg>
                          <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("coverage.search")}
                            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                          />
                        </label>
                        <FilterSelect
                          options={filterOptions}
                          value={filter}
                          onChange={(value) => setParams({ filter: value })}
                          ariaLabel={t("coverage.filter.label")}
                          minWidthClass="min-w-[13rem]"
                        />
                        <div className="flex items-center gap-2">
                          <span className={`hidden sm:inline ${META_LABEL}`}>
                            {t("coverage.ordering.label")}
                          </span>
                          <FilterSelect
                            options={orderingOptions}
                            value={ordering}
                            onChange={(value) => setParams({ ordering: value })}
                            ariaLabel={t("coverage.ordering.label")}
                            minWidthClass="min-w-[13rem]"
                          />
                        </div>
                      </div>

                      {/* Rows already on screen stay on screen while the next
                          page loads. Blanking back to a skeleton on every
                          filter change makes the table strobe, and the thing
                          being compared is the row that just left. */}
                      <div aria-busy={refetching} className={refetching ? STALE : ""}>
                        {rows.length === 0 ? (
                          <div className="px-6 py-16 text-center">
                            <p className="text-sm font-black text-slate-700">
                              {isFiltered ? t("coverage.empty") : t("coverage.emptyRosterTitle")}
                            </p>
                            <p className="mx-auto mt-1 max-w-sm text-xs font-medium text-slate-500">
                              {isFiltered ? t("coverage.emptyHint") : t("coverage.emptyRosterBody")}
                            </p>
                            {isFiltered && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSearch("");
                                  setParams({ filter: "all", search: "" });
                                }}
                                className={`mt-4 rounded-md px-1 text-sm font-bold text-indigo-600 hover:text-indigo-700 ${FOCUS_RING}`}
                              >
                                {t("common.clearFilters")}
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="hidden overflow-x-auto md:block">
                              <PersonTable
                                rows={rows}
                                caption={t("coverage.tableCaption")}
                                ordering={ordering}
                                onOrdering={(value) => setParams({ ordering: value })}
                                onOpenVisits={openVisits}
                              />
                            </div>
                            <div className="md:hidden">
                              <PersonCards rows={rows} onOpenVisits={openVisits} />
                            </div>
                          </>
                        )}
                      </div>

                      {totalPages > 1 && (
                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
                          <p className="text-xs text-slate-500">
                            {t("users.pageInfo", {
                              from: (page - 1) * COVERAGE_PAGE_SIZE + 1,
                              to: Math.min(page * COVERAGE_PAGE_SIZE, count),
                              total: count,
                            })}
                          </p>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setParams({ page: page - 1 }, { resetPage: false })}
                              disabled={page <= 1 || refetching}
                              className={`rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 ${FOCUS_RING}`}
                            >
                              {t("users.prev")}
                            </button>
                            <span className="px-2 text-xs font-semibold text-slate-500">
                              {t("users.pageOf", { page, totalPages })}
                            </span>
                            <button
                              type="button"
                              onClick={() => setParams({ page: page + 1 }, { resetPage: false })}
                              disabled={page >= totalPages || refetching}
                              className={`rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 ${FOCUS_RING}`}
                            >
                              {t("users.next")}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </section>

                {!isNetwork && guests.length > 0 && (
                  <section className={`${CARD} overflow-hidden`} aria-labelledby="coverage-guests-title">
                    <div className="border-b border-slate-100 px-5 py-4">
                      <h3 id="coverage-guests-title" className="text-lg font-bold text-slate-900">
                        {t("coverage.guestsTitle")}
                      </h3>
                      <p className="mt-0.5 max-w-2xl text-xs font-medium text-slate-500">
                        {t("coverage.guestsHint")}
                      </p>
                    </div>
                    {/* No rose rail down here. It means "this is the school's
                        coverage gap", and a guest is not in the roster that gap
                        is measured against — `totals.uncovered` does not count
                        them either. A guest nobody happened to visit HERE is a
                        fact about a visitor, not a teacher going unobserved. */}
                    <div className="hidden overflow-x-auto md:block">
                      <PersonTable
                        rows={guests}
                        caption={t("coverage.guestsTitle")}
                        ordering={ordering}
                        onOrdering={(value) => setParams({ ordering: value })}
                        onOpenVisits={openVisits}
                        sortable={false}
                        flagUncovered={false}
                      />
                    </div>
                    <div className="md:hidden">
                      <PersonCards rows={guests} onOpenVisits={openVisits} flagUncovered={false} />
                    </div>
                  </section>
                )}

                {/* The rule is on screen, not in a wiki, because the number it
                    produces has to survive being questioned at a staff meeting
                    by the person it counts. */}
                {!isNetwork && (
                  <section className="rounded-2xl border border-slate-200 bg-white/60 px-5 py-4">
                    <h3 className={META_LABEL}>{t("coverage.legend.title")}</h3>
                    <ul className="mt-2 space-y-1.5 text-xs font-medium leading-relaxed text-slate-500">
                      <li>{t("coverage.legend.counted")}</li>
                      <li>{t("coverage.legend.closed")}</li>
                      <li>{t("coverage.legend.departed")}</li>
                    </ul>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </div>

      {schedule && (
        <ScheduleObservationModal
          mainObserver={schedule.person}
          // The modal fetches who may conduct itself, paged and searched on the
          // server. It used to be handed this table's rows, which made the
          // picker one page of twenty — everyone else appeared only after
          // paging the table underneath, and a filter narrowed it further.
          pickObserver
          onClose={() => setSchedule(null)}
          onSubmitted={() => {
            setSchedule(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {drawer && (
        <VisitsDrawer
          // Keyed on the pair, so clicking a second number re-opens on THAT
          // column instead of leaving the drawer on whichever group the
          // viewer last switched it to.
          key={`${drawer.row.person?.uuid}:${drawer.relation}`}
          row={drawer.row}
          relation={drawer.relation}
          organizationUuid={effectiveOrgUuid}
          period={data?.period}
          canSchedule={canSchedule}
          canDelete={canDelete}
          // The table's counts include the visit that just went.
          onDeleted={() => setReloadKey((k) => k + 1)}
          onAssign={(relation) => {
            // Only the observer group names an observer. Under «как учитель»
            // this row is the person being visited, and pre-filling them as
            // the one who runs the visit would book a self-observation.
            const person = relation === "conducted" ? drawer.row.person : undefined;
            setDrawer(null);
            setSchedule({ person });
          }}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}
